import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Coffee, UtensilsCrossed, Refrigerator, Sandwich } from "lucide-react";
import { useT } from "@/context/I18nContext";

const SERVICES = [
  { id: "buffet", icon: UtensilsCrossed, img: "catering/buffet.jpg" },
  { id: "cold-breakfast", icon: Coffee, img: "catering/cold-breakfast.jpg" },
  { id: "platters", icon: Sandwich, img: "catering/platters.jpg" },
  { id: "smart-fridge", icon: Refrigerator, img: "catering/smart-fridge.jpg" },
];
const media = (p, w) => `${API}/media?path=${encodeURIComponent(p)}${w ? `&w=${w}` : ""}`;
const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brunchi-green)]";

export default function CorporateCatering() {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "",
    people_count: 20, event_date: "", service: "buffet", message: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/catering/quote", { ...form, people_count: parseInt(form.people_count) || 1 });
      setSent(true);
      toast.success(t("catering.sent"));
    } catch (err) {
      toast.error(apiError(err, t("contact.failed")));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="catering-page">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">{t("catering.eyebrow")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">{t("catering.title")}</h1>
          <p className="mt-5 text-base md:text-lg text-[color:var(--brunchi-ink-soft)]">{t("catering.intro")}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 grid sm:grid-cols-2 gap-5" data-testid="catering-gallery">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <figure key={s.id} className={`group relative overflow-hidden rounded-2xl bg-[color:var(--brunchi-stone)] ${i % 3 === 0 ? "sm:row-span-1" : ""}`} data-testid={`catering-card-${s.id}`}>
              <img src={media(s.img, 1000)} alt={t(`catering.s_${s.id}`)} loading="lazy"
                className="w-full aspect-[3/2] object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent text-white">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] opacity-90"><Icon size={14} /> {t(`catering.s_${s.id}`)}</div>
                <p className="mt-1 text-sm opacity-90">{t(`catering.d_${s.id}`)}</p>
              </figcaption>
            </figure>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <h2 className="font-display text-3xl tracking-tight">{t("catering.how_title")}</h2>
          <ol className="mt-5 space-y-4 text-sm text-[color:var(--brunchi-ink-soft)]">
            {[1, 2, 3].map((n) => (
              <li key={n} className="flex gap-3">
                <span className="font-mono text-[color:var(--brunchi-green-hover)]">0{n}</span>
                <span>{t(`catering.step_${n}`)}</span>
              </li>
            ))}
          </ol>
          <p className="mt-8 text-sm text-[color:var(--brunchi-ink-soft)]">
            {t("catering.contact_hint")} <Link to="/contact" className="underline text-[color:var(--brunchi-green-hover)]" data-testid="catering-contact-link">{t("nav.contact")}</Link>
          </p>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-[color:var(--brunchi-stone)] p-6 sm:p-8" id="quote">
          {sent ? (
            <div className="py-16 text-center" data-testid="catering-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[color:var(--brunchi-green-light)] flex items-center justify-center text-[color:var(--brunchi-green-hover)]"><Building2 size={22} /></div>
              <h2 className="mt-5 font-display text-2xl">{t("contact.thanks_title")}</h2>
              <p className="mt-2 text-[color:var(--brunchi-ink-soft)]">{t("catering.thanks_body")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="catering-form">
              <h2 className="font-display text-2xl mb-2">{t("catering.form_title")}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input required placeholder={t("catering.f_company")} value={form.company} onChange={set("company")} className={input} data-testid="catering-company" />
                <input required placeholder={t("contact.f_name")} value={form.contact_name} onChange={set("contact_name")} className={input} data-testid="catering-contact" />
                <input required type="email" placeholder={t("contact.f_email")} value={form.email} onChange={set("email")} className={input} data-testid="catering-email" />
                <input placeholder={t("contact.f_phone")} value={form.phone} onChange={set("phone")} className={input} data-testid="catering-phone" />
                <select value={form.service} onChange={set("service")} className={input} data-testid="catering-service">
                  {SERVICES.map((s) => <option key={s.id} value={s.id}>{t(`catering.s_${s.id}`)}</option>)}
                </select>
                <input type="number" min="1" required placeholder={t("catering.f_people")} value={form.people_count} onChange={set("people_count")} className={input} data-testid="catering-people" />
                <input type="date" value={form.event_date} onChange={set("event_date")} className={`${input} sm:col-span-2`} data-testid="catering-date" />
                <textarea rows={4} placeholder={t("catering.f_message")} value={form.message} onChange={set("message")} className={`${input} sm:col-span-2`} data-testid="catering-message" />
              </div>
              <button disabled={busy} type="submit" data-testid="catering-submit-btn"
                className="w-full py-3.5 rounded-full pill-primary font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40">
                <Building2 size={16} /> {busy ? t("contact.sending") : t("catering.request")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
